import { useMemo, useCallback } from 'react';
import { useTranslation } from '@wix/yoshi-flow-editor';

import { DispatchType } from '../../../../types/businessTypes';
import {
  isDispatchTypeSelectable,
  mapFulfilmentTypeToDispatchType,
} from '../../../../services/fulfillmentsService/helpers';
import { RequestStatus } from '../../../../utils/wrapRequest';
import { useOnlineOrdersStorage } from '../../storage';
import type {
  FulfillmentPickerOption,
  FulfillmentPickerProps,
} from '../../../../components-shared/FulfillmentPicker';

const LABEL_KEY_BY_TYPE: Record<DispatchType, string> = {
  [DispatchType.PICKUP]: 'header_olo.pickup',
  [DispatchType.DELIVERY]: 'header_olo.delivery',
  [DispatchType.DINE_IN]: 'header_olo.dineIn',
};

export const useHooks = (): FulfillmentPickerProps => {
  const { t } = useTranslation();
  const {
    fulfillmentMethods,
    selectedDispatchType,
    dispatchesInfo,
    fetchTimeSlotsStatus,
    fetchFulfillmentMethodsStatus,
    operations,
    currentOperation,
    acceptingDispatchTypesByOperationId,
    openFulfillmentModal,
  } = useOnlineOrdersStorage();

  const configuredDispatchTypes = useMemo<DispatchType[]>(() => {
    const seen = new Set<DispatchType>();
    const result: DispatchType[] = [];

    for (const method of fulfillmentMethods) {
      if (!method.enabled || !method.type) {
        continue;
      }

      const type = mapFulfilmentTypeToDispatchType(method.type);

      if (!seen.has(type)) {
        seen.add(type);
        result.push(type);
      }
    }

    return result;
  }, [fulfillmentMethods]);

  const isDispatchInfoResolved =
    fetchTimeSlotsStatus === RequestStatus.RESOLVED &&
    fetchFulfillmentMethodsStatus === RequestStatus.RESOLVED;
  const isMultiLocation = operations.length > 1;

  const options = useMemo<FulfillmentPickerOption[]>(
    () =>
      configuredDispatchTypes.map((type) => ({
        type,
        label: t(LABEL_KEY_BY_TYPE[type]),
        disabled:
          isDispatchInfoResolved &&
          !isMultiLocation &&
          !isDispatchTypeSelectable(type, dispatchesInfo[type]),
      })),
    [
      configuredDispatchTypes,
      dispatchesInfo,
      isDispatchInfoResolved,
      isMultiLocation,
      t,
    ],
  );

  const isLoading = fetchFulfillmentMethodsStatus === RequestStatus.LOADING;

  const selectedOption = options.find(
    (option) => option.type === selectedDispatchType,
  );
  const selectedType =
    selectedOption && !selectedOption.disabled
      ? selectedDispatchType
      : undefined;

  const onSelect = useCallback(
    (type: DispatchType) => {
      if (!currentOperation) {
        return;
      }

      openFulfillmentModal({
        operations,
        currentOperation,
        dispatchesInfo,
        selectedDispatchType: type,
        acceptingDispatchTypesByOperationId,
      });
    },
    [
      operations,
      currentOperation,
      dispatchesInfo,
      acceptingDispatchTypesByOperationId,
      openFulfillmentModal,
    ],
  );

  return {
    options,
    selectedType,
    isHidden: isLoading || options.length === 0,
    onSelect,
  };
};
